import React, { useRef } from "react";
import Icon from "components/icon";
import Typography from "components/typography";

export interface ImageUploadPreviewProps {
  value?: string | File | null;
  fileName?: string;
  onUpload: (file: File) => void;
  onRemove: () => void;
  isUploading?: boolean;
  uploadingText?: string;
  dropText?: string;
  helperText?: string;
  accept?: string;
  error?: string | boolean;
  disabled?: boolean;
  id?: string;
  dataTestId?: string;
  className?: string;
}

export const extractFileNameFromUrl = (url?: string | null): string => {
  if (!url || typeof url !== "string") return "";
  if (url.startsWith("data:")) return "";
  try {
    const cleanUrl = url.split("?")[0].split("#")[0];
    const segments = cleanUrl.split("/").filter(Boolean);
    const lastSegment = segments[segments.length - 1];
    return decodeURIComponent(lastSegment || "");
  } catch {
    const parts = url.split("/");
    return parts[parts.length - 1] || "";
  }
};

export const ImageUploadPreview: React.FC<ImageUploadPreviewProps> = ({
  value,
  fileName,
  onUpload,
  onRemove,
  isUploading = false,
  uploadingText = "Uploading...",
  dropText = "Click to upload",
  helperText = "Format: JPG, PNG (Max. 2MB)",
  accept = ".jpg,.jpeg,.png,image/jpeg,image/png,image/jpg",
  error,
  disabled = false,
  id = "image-upload-input",
  dataTestId = "upload-image-preview",
  className = "",
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const imageUrl =
    typeof value === "string"
      ? value
      : value instanceof File
      ? URL.createObjectURL(value)
      : "";

  const displayFileName =
    fileName ||
    (value instanceof File
      ? value.name
      : extractFileNameFromUrl(typeof value === "string" ? value : ""));

  const hasImage = Boolean(imageUrl && imageUrl.trim() !== "");

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onUpload(file);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleContainerClick = () => {
    if (!disabled && !isUploading && !hasImage) {
      fileInputRef.current?.click();
    }
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRemove();
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className={`flex flex-col gap-1.5 w-full ${className}`}>
      <div
        data-testid={dataTestId}
        className={`h-[180px] w-full rounded-lg border border-dashed p-3 flex flex-col items-center justify-center text-center gap-2 transition-colors relative overflow-hidden ${
          disabled
            ? "bg-neutral-100 border-neutral-300 cursor-not-allowed opacity-60"
            : hasImage
            ? "border-border bg-whiteScale-95"
            : error
            ? "border-error-500 bg-error-50 cursor-pointer"
            : "border-border bg-whiteScale-95 hover:bg-whiteScale-90 cursor-pointer"
        }`}
        onClick={handleContainerClick}
      >
        {isUploading ? (
          <div className="flex flex-col items-center gap-2">
            <Icon name="refresh" size={24} className="animate-spin text-primary-500" />
            <Typography variant="bodySmall" tone="muted">
              {uploadingText}
            </Typography>
          </div>
        ) : hasImage ? (
          <div className="relative group w-full h-full flex items-center justify-center">
            <img
              src={imageUrl}
              alt="Uploaded Preview"
              className="w-full h-full object-contain rounded-lg"
            />
            {!disabled && (
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-lg">
                <button
                  type="button"
                  className="bg-error-500 text-white p-2 rounded-full hover:bg-error-600 transition-colors cursor-pointer shadow-md"
                  onClick={handleRemove}
                  title="Remove image"
                  data-testid="btn-remove-image"
                >
                  <Icon name="trash" size={20} />
                </button>
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="h-10 w-10 rounded-md bg-primary-500 text-white inline-flex items-center justify-center shadow-sm">
              <Icon name="document-upload" size={18} />
            </div>
            <Typography variant="body" tone="muted" className="text-[11px] leading-tight">
              {dropText}
            </Typography>
            <Typography variant="bodySmall" tone="muted" className="text-[9px]">
              {helperText}
            </Typography>
          </>
        )}
      </div>

      <input
        ref={fileInputRef}
        id={id}
        type="file"
        accept={accept}
        className="hidden"
        disabled={disabled || isUploading}
        onChange={handleFileChange}
      />

      {hasImage && displayFileName && (
        <Typography
          variant="bodySmall"
          tone="muted"
          className="truncate max-w-full text-center text-xs block px-1"
          title={displayFileName}
          data-testid="text-image-filename"
        >
          {displayFileName}
        </Typography>
      )}

      {typeof error === "string" && error && (
        <Typography variant="bodySmall" className="text-error-500 text-xs text-center">
          {error}
        </Typography>
      )}
    </div>
  );
};

export default ImageUploadPreview;
